import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  CompartmentUserListInitialState,
  CompartmentAssignedUserListLoad,
  CompartmentAvailableUserListLoad,
  CompartmentUserListAssignUsers,
  CompartmentUserListDeassignUsers,
  CompartmentUserListResetAvailableList,
  CompartmentUserListUpdateUserPermissions,
} from './compartment-user-list.actions';
import { LIST_SERVER_PAGINATION_INITIAL_STATE, ListServerPaginationStateModel } from '../../../../../../common/models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { CompartmentUserListService } from './compartment-user-list.service';
import { UserSelectionItem } from '../../../../../../container-components';
import { CustomHttpErrorResponse, Error } from '../../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { ListSearchCriteriaFacade, ListSearchCriteriaType } from '../../../../list-search-criteria';
import { CompartmentAssignedUserListSearchCriteria } from '../../models';

export interface CompartmentUserListStateModel {
  assignedList: ListServerPaginationStateModel<UserSelectionItem>;
  availableList: ListServerPaginationStateModel<UserSelectionItem>;
  loaded: boolean;
}

const initialState: CompartmentUserListStateModel = {
  assignedList: LIST_SERVER_PAGINATION_INITIAL_STATE,
  availableList: LIST_SERVER_PAGINATION_INITIAL_STATE,
  loaded: false,
};

@State<CompartmentUserListStateModel>({
  name: 'compartmentUserList',
  defaults: initialState,
})
@Injectable()
export class CompartmentUserListState {
  constructor(
    private compartmentUserListService: CompartmentUserListService,
    private notificationService: NotificationService,
    private listSearchCriteriaFacade: ListSearchCriteriaFacade,
  ) {}

  @Selector()
  static getAssignedUsers(state: CompartmentUserListStateModel): ListServerPaginationStateModel<UserSelectionItem> {
    return state.assignedList;
  }

  @Selector()
  static getAvailableUsers(state: CompartmentUserListStateModel): ListServerPaginationStateModel<UserSelectionItem> {
    return state.availableList;
  }

  @Selector()
  static getLoaded(state: CompartmentUserListStateModel): boolean {
    return state.loaded;
  }

  @Action(CompartmentAssignedUserListLoad)
  getAssignedUsers(
    ctx: StateContext<CompartmentUserListStateModel>,
    { payload }: CompartmentAssignedUserListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentUserListService.getCompartmentAssignedUserList(payload).pipe(
      take(1),
      map((list: ListServerPaginationStateModel<UserSelectionItem>) => {
        ctx.patchState({ assignedList: list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentAvailableUserListLoad)
  getAvailableUsers(
    ctx: StateContext<CompartmentUserListStateModel>,
    { payload }: CompartmentAvailableUserListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentUserListService.getCompartmentAvailableUserList(payload).pipe(
      take(1),
      map((list: ListServerPaginationStateModel<UserSelectionItem>) => {
        ctx.patchState({ availableList: list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentUserListAssignUsers)
  assignAvailableUsersToCompartment(
    ctx: StateContext<CompartmentUserListStateModel>,
    { payload }: CompartmentUserListAssignUsers,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentUserListService.assignUsers(payload).pipe(
      take(1),
      map((data: UserSelectionItem) => {
        payload.dialog.close();
        this.notificationService.success('api.compartment.success.assignUsers');
        const { hubId, compartmentId } = payload;
        ctx.patchState({ ...data, loaded: true });
        this.updateList(ctx, hubId, compartmentId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentUserListDeassignUsers)
  deassignUsersFromCompartment(
    ctx: StateContext<CompartmentUserListStateModel>,
    { payload }: CompartmentUserListDeassignUsers,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentUserListService.deassignUsers(payload).pipe(
      take(1),
      map((data: UserSelectionItem) => {
        this.notificationService.success('api.compartment.success.deassignUsers');
        const { hubId, compartmentId } = payload;
        ctx.patchState({ ...data, loaded: true });
        this.updateList(ctx, hubId, compartmentId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentUserListUpdateUserPermissions)
  updateUserPermissions(
    ctx: StateContext<CompartmentUserListStateModel>,
    { payload }: CompartmentUserListUpdateUserPermissions,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentUserListService.updateUser(payload).pipe(
      take(1),
      map((data: UserSelectionItem) => {
        this.notificationService.success('api.compartment.success.updateUser');
        const { hubId, compartmentId } = payload;
        ctx.patchState({ ...data, loaded: true });
        this.updateList(ctx, hubId, compartmentId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentUserListResetAvailableList)
  resetAvailableList(ctx: StateContext<CompartmentUserListStateModel>): void {
    ctx.patchState({
      availableList: LIST_SERVER_PAGINATION_INITIAL_STATE,
      loaded: true,
    });
  }

  @Action(CompartmentUserListInitialState)
  setInitialState(ctx: StateContext<CompartmentUserListStateModel>): void {
    ctx.setState(initialState);
  }

  private updateList(ctx: StateContext<CompartmentUserListStateModel>, hubId: number, compartmentId: number): void {
    this.listSearchCriteriaFacade
      .getSearchCriteriaByListType(ListSearchCriteriaType.CompartmentAssignedUsers)
      .subscribe((searchCriteria: CompartmentAssignedUserListSearchCriteria) =>
        ctx.dispatch(new CompartmentAssignedUserListLoad({ hubId, compartmentId, searchCriteria })),
      );
  }
}
